import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildFilterStorageKey, FILTER_TABLE_STATE_KEYS as TABLE_STATE_KEYS } from '@/utils/filterSnapshot';

/**
 * Persists filter + table state URL params to localStorage per tenant per user per page.
 * On mount: restores saved params if URL has none of the tracked keys.
 * Call saveSnapshot(next) after applying filters or changing page/pageSize.
 * Call clearSnapshot() when user explicitly clears filters.
 */
export function useFilterPersistence(
  tableKey: string,
  userId: string | undefined,
  paramKeys: string[],
  defaultFilters?: Record<string, string>,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [readySignature, setReadySignature] = useState<string>();
  const storageKey = buildFilterStorageKey(tableKey, userId);
  const signature = `${storageKey}?${searchParams.toString()}`;
  const isReady = readySignature === signature;
  /** Bỏ qua ĐÚNG một lần khôi phục ngay sau khi người dùng chủ động xoá bộ lọc. */
  const skipNextRestore = useRef(false);

  // paramKeys là mảng mới mỗi render ở một số call site → giữ qua ref thay vì đưa vào deps.
  const paramKeysRef = useRef(paramKeys);
  paramKeysRef.current = paramKeys;

  useEffect(() => {
    let waitingForRestoredUrl = false;
    // Chạy trên MỌI thay đổi URL, không chỉ lúc mount. Bấm menu "Đơn hàng" khi đang
    // đứng ở /orders có filter sẽ điều hướng sang /orders trống mà KHÔNG unmount trang,
    // nên guard chạy-một-lần cũ khiến bộ lọc mất sạch và không khôi phục lại được.
    if (skipNextRestore.current) {
      skipNextRestore.current = false;
      setReadySignature(signature);
      return;
    }
    // CHỈ xét filter key khi quyết định có restore hay không. Tính cả page/pageSize
    // vào đây sẽ khiến mọi URL mang sẵn `?page=…` bị bỏ qua cả restore LẪN seeding
    // defaultFilters — trạng thái mặc định khi đó phụ thuộc đường vào trang.
    const hasAny = paramKeysRef.current.some((k) => searchParams.get(k));
    if (hasAny) {
      setReadySignature(signature);
      return;
    }
    try {
      const saved = localStorage.getItem(storageKey);
      let entries: Record<string, string> = {};
      if (saved) {
        entries = JSON.parse(saved) as Record<string, string>;
      } else if (defaultFilters) {
        entries = defaultFilters;
      }
      const hasEntries = Object.values(entries).some(Boolean);
      if (!hasEntries) return;
      const next = new URLSearchParams(searchParams);
      Object.entries(entries).forEach(([k, v]) => {
        // Param có sẵn trên URL luôn thắng snapshot (vd link chia sẻ kèm ?page=3).
        if (searchParams.get(k)) return;
        if (v) next.set(k, v);
        else next.delete(k);
      });
      if (next.toString() !== searchParams.toString()) {
        waitingForRestoredUrl = true;
        setSearchParams(next, { replace: true });
      }
      if (!saved && defaultFilters) {
        localStorage.setItem(storageKey, JSON.stringify(entries));
      }
    } catch {
      // ignore storage errors
    } finally {
      if (!waitingForRestoredUrl) setReadySignature(signature);
    }
    // Hội tụ sau 1 vòng: khôi phục xong thì URL đã có filter key nên lượt sau `hasAny`
    // đúng và effect thoát ngay.
  }, [searchParams, signature, storageKey, setSearchParams]);

  // useCallback là bắt buộc, không phải tối ưu vặt: useListPageState đưa 2 hàm này vào deps
  // của applyFilters/clearFilters. Nếu chúng đổi identity mỗi render thì applyFilters cũng đổi,
  // kéo theo mọi useMemo phía dưới (vd mảng cột của Orders) vỡ ở từng render.

  /** Call with the new URLSearchParams after applying filters / changing table state */
  const saveSnapshot = useCallback(
    (next: URLSearchParams) => {
      try {
        const snapshot: Record<string, string> = {};
        [...paramKeysRef.current, ...TABLE_STATE_KEYS].forEach((k) => {
          const v = next.get(k);
          if (v) snapshot[k] = v;
        });
        localStorage.setItem(storageKey, JSON.stringify(snapshot));
      } catch {
        // ignore
      }
    },
    [storageKey],
  );

  /** Call when user explicitly clears all filters */
  const clearSnapshot = useCallback(() => {
    // Chặn đúng một lượt khôi phục kế tiếp: người dùng vừa bấm "Xoá bộ lọc" mà effect
    // lại seed ngay defaultFilters thì thao tác xoá trông như không ăn.
    skipNextRestore.current = true;
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [storageKey]);

  return { saveSnapshot, clearSnapshot, isReady };
}
